import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatINR } from "@/lib/api";
import { CheckCircle2, CreditCard, Truck, Tag } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["ADDRESS", "REVIEW", "PAYMENT"];
const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    if (document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`)) {
      const wait = setInterval(() => {
        if (window.Razorpay) { clearInterval(wait); resolve(true); }
      }, 50);
      setTimeout(() => { clearInterval(wait); resolve(!!window.Razorpay); }, 8000);
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { user, refresh: refreshUser } = useAuth();
  const { items, subtotal, refresh: refreshCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [placing, setPlacing] = useState(false);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-5xl tracking-tighter mb-4">CART IS EMPTY</h1>
        <Link to="/shop/all" className="inline-block bg-black text-white px-8 py-4 font-display tracking-widest text-sm">SHOP NOW</Link>
      </div>
    );
  }

  const discount = appliedCoupon?.discount || 0;
  const baseShipping = subtotal >= 1499 ? 0 : 99;
  const shipping = appliedCoupon?.coupon?.type === "shipping" ? 0 : baseShipping;
  const walletRedeem = useWallet ? Math.min(user?.wallet_points || 0, Math.floor(subtotal * 0.1)) : 0;
  const total = Math.max(0, subtotal - discount - walletRedeem + shipping);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.coupon.code} applied`);
    } catch (e) {
      toast.error(formatApiError(e));
      setAppliedCoupon(null);
    }
  };

  const place = async () => {
    setPlacing(true);
    try {
      // 1. Stage internal order + create Razorpay order on server
      const { data } = await api.post("/checkout", {
        address,
        coupon_code: appliedCoupon?.coupon?.code || null,
        use_wallet: useWallet,
        payment_method: "RAZORPAY",
      });

      // Zero-rupee orders (full wallet redemption) skip the gateway.
      if (!data?.razorpay?.order_id || data.razorpay.amount === 0) {
        await refreshCart();
        await refreshUser();
        navigate(`/order/${data.order.id}`, { replace: true });
        return;
      }

      // 2. Load Razorpay checkout script
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load payment gateway. Please retry.");
        return;
      }

      // 3. Open Razorpay modal
      const rzp = new window.Razorpay({
        key: data.razorpay.key_id,
        amount: data.razorpay.amount,
        currency: data.razorpay.currency,
        order_id: data.razorpay.order_id,
        name: "BYONDELMNTS",
        description: `Order ${data.order.order_no}`,
        image: "/logo-be-512.png",
        prefill: {
          name: address.full_name || user?.name || "",
          email: user?.email || "",
          contact: address.phone || user?.phone || "",
        },
        notes: { our_order_id: data.order.id },
        theme: { color: "#FF3B30" },
        handler: async (resp) => {
          try {
            const verify = await api.post("/payments/verify", {
              our_order_id: data.order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refreshCart();
            await refreshUser();
            navigate(`/order/${verify.data.order.id}`, { replace: true });
          } catch (err) {
            toast.error(formatApiError(err));
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.message("Payment cancelled. You can retry anytime.");
            setPlacing(false);
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setPlacing(false);
    }
  };

  const validAddress = address.full_name && address.phone && address.line1 && address.city && address.state && address.pincode;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="checkout-page">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-4 mb-12">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${i <= step ? "text-black" : "text-black/30"}`}>
              <span className={`w-8 h-8 border ${i <= step ? "border-black bg-black text-white" : "border-black/30"} flex items-center justify-center font-display`}>{i + 1}</span>
              <span className="font-display tracking-widest text-xs">{s}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`w-12 h-px ${i < step ? "bg-black" : "bg-black/20"}`} />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          {step === 0 && (
            <div className="border border-black/15 p-6 space-y-4" data-testid="address-step">
              <h2 className="font-display text-3xl tracking-tighter mb-2"><Truck size={20} className="inline mr-2 text-[#FF3B30]" /> SHIPPING ADDRESS</h2>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="FULL NAME" value={address.full_name} onChange={(v) => setAddress({ ...address, full_name: v })} testId="addr-name" />
                <Field label="PHONE" value={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} testId="addr-phone" />
              </div>
              <Field label="ADDRESS LINE 1" value={address.line1} onChange={(v) => setAddress({ ...address, line1: v })} testId="addr-line1" />
              <Field label="ADDRESS LINE 2 (OPTIONAL)" value={address.line2} onChange={(v) => setAddress({ ...address, line2: v })} testId="addr-line2" />
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="CITY" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} testId="addr-city" />
                <Field label="STATE" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} testId="addr-state" />
                <Field label="PINCODE" value={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} testId="addr-pin" />
              </div>
              <button
                onClick={() => setStep(1)}
                disabled={!validAddress}
                className="bg-[#FF3B30] text-white px-8 py-3 font-display tracking-widest text-sm disabled:opacity-50 hover:bg-black"
                data-testid="address-continue"
              >
                CONTINUE TO REVIEW
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="border border-black/15 p-6 space-y-6" data-testid="review-step">
              <h2 className="font-display text-3xl tracking-tighter">REVIEW ORDER</h2>
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={`${it.product_id}-${it.size}-${it.color}`} className="grid grid-cols-[64px_1fr_auto] gap-4 items-center border-b border-black/10 pb-3">
                    <img src={it.product?.images?.[0]} alt="" className="w-16 aspect-[3/4] object-cover" />
                    <div>
                      <div className="font-display tracking-tight uppercase">{it.product?.name}</div>
                      <div className="text-xs font-mono text-black/40">SIZE {it.size} · {it.color} · QTY {it.qty}</div>
                    </div>
                    <div className="font-display">{formatINR((it.product?.price || 0) * it.qty)}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="font-display text-xs tracking-widest text-black/60 mb-2 flex items-center gap-2"><Tag size={14} /> APPLY COUPON</div>
                <div className="flex gap-2">
                  <input
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder="FIRST10"
                    className="flex-1 bg-transparent border border-black/30 px-4 py-2 font-display tracking-widest text-sm"
                    data-testid="coupon-input"
                  />
                  <button onClick={applyCoupon} className="bg-black text-white px-6 font-display tracking-widest text-xs" data-testid="apply-coupon-btn">APPLY</button>
                </div>
                {appliedCoupon && (
                  <div className="mt-2 text-xs text-[#FF3B30] font-mono">✓ {appliedCoupon.coupon.code} — {formatINR(appliedCoupon.discount)} off</div>
                )}
                <p className="text-xs text-black/40 mt-2 font-mono">TRY: FIRST10, FREESHIP, DROP500</p>
              </div>
              {user?.wallet_points > 0 && (
                <label className="flex items-center gap-3 cursor-pointer border border-black/15 p-4" data-testid="wallet-toggle">
                  <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="accent-[#FF3B30]" />
                  <div>
                    <div className="font-display tracking-widest text-sm">USE ELMNTS POINTS</div>
                    <div className="text-xs font-mono text-black/50">{user.wallet_points} POINTS AVAILABLE · MAX 10% OF ORDER</div>
                  </div>
                </label>
              )}
              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="border border-black/30 px-6 py-3 font-display tracking-widest text-sm">BACK</button>
                <button onClick={() => setStep(2)} className="bg-[#FF3B30] text-white px-8 py-3 font-display tracking-widest text-sm hover:bg-black" data-testid="review-continue">CONTINUE TO PAYMENT</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="border border-black/15 p-6 space-y-6" data-testid="payment-step">
              <h2 className="font-display text-3xl tracking-tighter"><CreditCard size={20} className="inline mr-2 text-[#FF3B30]" /> PAYMENT</h2>
              <div className="border border-black/30 p-6 bg-black/5">
                <div className="font-display tracking-widest text-sm">RAZORPAY SECURE CHECKOUT</div>
                <p className="text-xs text-black/60 mt-2 font-mono">CARDS · UPI · NETBANKING · WALLETS · EMI</p>
                <p className="text-xs text-black/70 mt-3 leading-relaxed">
                  You&apos;ll be securely redirected to Razorpay to complete payment. Test mode is active — use
                  card <span className="font-mono text-black">4111 1111 1111 1111</span> (any CVV, any future expiry)
                  or UPI ID <span className="font-mono text-black">success@razorpay</span> to simulate a successful payment.
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="border border-black/30 px-6 py-3 font-display tracking-widest text-sm">BACK</button>
                <button onClick={place} disabled={placing} className="bg-[#FF3B30] text-white px-8 py-3 font-display tracking-widest text-sm hover:bg-black disabled:opacity-60" data-testid="place-order-btn">
                  {placing ? "PROCESSING..." : `PAY ${formatINR(total)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border border-black/15 p-6 h-fit lg:sticky lg:top-32">
          <h3 className="font-display text-2xl tracking-tighter mb-4">SUMMARY</h3>
          <div className="space-y-2 text-sm">
            <Row l="SUBTOTAL" v={formatINR(subtotal)} />
            {discount > 0 && <Row l={`DISCOUNT (${appliedCoupon?.coupon?.code})`} v={`-${formatINR(discount)}`} />}
            {walletRedeem > 0 && <Row l="POINTS APPLIED" v={`-${formatINR(walletRedeem)}`} />}
            <Row l="SHIPPING" v={shipping === 0 ? "FREE" : formatINR(shipping)} />
            <div className="border-t border-black/15 pt-3 mt-3">
              <Row l="TOTAL" v={formatINR(total)} big />
            </div>
          </div>
          <div className="mt-6 text-xs font-mono text-black/40 flex items-center gap-2">
            <CheckCircle2 size={14} className="text-[#FF3B30]" /> Secure SSL · 7-day returns
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[10px] text-black/50">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-2 mt-1"
        data-testid={testId}
      />
    </label>
  );
}

function Row({ l, v, big }) {
  return (
    <div className={`flex justify-between ${big ? "font-display text-xl" : ""}`}>
      <span className={big ? "" : "text-black/60"}>{l}</span>
      <span>{v}</span>
    </div>
  );
}
